import {
  TArtifactLengthOptions,
  TReadingLevelOptions,
} from '@/hooks/promptCanvas/types';

export const getSelectedTextRange = (
  textRef: React.RefObject<HTMLDivElement>
): Promise<{ startIdx: number; endIdx: number } | null> => {
  return new Promise((resolve) => {
    // wait for the selection to be updated
    setTimeout(() => {
      const selection = window.getSelection();
      const selectionString = selection?.toString();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

      if (!selectionString || !range || !textRef.current) {
        resolve(null);
        return;
      }

      // Ensure the selection is within the prompt
      if (
        !textRef.current.contains(range.startContainer) ||
        !textRef.current.contains(range.endContainer)
      ) {
        resolve(null);
        return;
      }

      const promptText = textRef.current.innerText;

      // Helper function to calculate the absolute offset
      const getCharacterOffset = (
        node: Node,
        offset: number
      ): number | null => {
        let charCount = 0;
        const traverseNodes = (currentNode: Node): boolean => {
          if (currentNode === node) {
            charCount += offset;
            return true; // Stop traversal
          }

          if (currentNode.nodeType === Node.TEXT_NODE) {
            charCount += currentNode.textContent?.length || 0;
          } else {
            for (let i = 0; i < currentNode.childNodes.length; i++) {
              if (traverseNodes(currentNode.childNodes[i])) {
                return true;
              }
            }
          }
          return false;
        };

        if (!textRef.current) return null;
        traverseNodes(textRef.current);
        return charCount;
      };

      const startIdx = getCharacterOffset(
        range.startContainer,
        range.startOffset
      );
      const endIdx = getCharacterOffset(range.endContainer, range.endOffset);

      // Validate indices
      if (
        startIdx !== null &&
        endIdx !== null &&
        startIdx >= 0 &&
        endIdx >= startIdx &&
        endIdx <= promptText.length
      ) {
        resolve({ startIdx, endIdx });
      } else {
        resolve(null);
      }
    }, 0);
  });
};

export const getUserMessageFromAction = ({
  improvement_instruction,
  highlightedRange,
  readingLevel,
  artifactLength,
}: {
  improvement_instruction?: string;
  highlightedRange?: [number, number];
  readingLevel?: TReadingLevelOptions;
  artifactLength?: TArtifactLengthOptions;
}) => {
  const prefix = 'Update the message';
  if (improvement_instruction) {
    return improvement_instruction;
  } else if (highlightedRange) {
    return 'Update the highlighted text';
  } else if (readingLevel) {
    switch (readingLevel) {
      case 'child':
        return `${prefix} to be much simpler`;
      case 'teenager':
        return `${prefix} to be a little simpler`;
      case 'college':
        return `${prefix} to be a little more complex`;
      case 'phd':
        return `${prefix} to be much more complex`;
      default:
        return prefix;
    }
  } else if (artifactLength) {
    switch (artifactLength) {
      case 'shortest':
        return `${prefix} to be much shorter`;
      case 'short':
        return `${prefix} to be a little shorter`;
      case 'long':
        return `${prefix} to be a little longer`;
      case 'longest':
        return `${prefix} to be much longer`;
      default:
        return prefix;
    }
  }
  return prefix;
};

export const createHighlightFromIndexes = (
  highlightedRange: [number, number],
  viewingArtifactContent: string
): {
  prompt_chunk_start_index: number;
  prompt_chunk_end_index: number;
  prompt_chunk: string;
  highlight_text: string;
} => {
  const [startIdx, endIdx] = highlightedRange;
  const startChunkCharIndex = Math.max(0, startIdx - 200);
  const endChunkCharIndex = Math.min(
    viewingArtifactContent.length,
    endIdx + 200
  );

  return {
    prompt_chunk_start_index: startChunkCharIndex,
    prompt_chunk_end_index: endChunkCharIndex,
    prompt_chunk: viewingArtifactContent.slice(
      startChunkCharIndex,
      endChunkCharIndex
    ),
    highlight_text: viewingArtifactContent.slice(startIdx, endIdx),
  };
};
